// Build: V246 — clean full rebuild; regenerates all lazy route chunks
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/ScrollToTop';
import AuthReturnForwarder from './components/AuthReturnForwarder';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
// Aria widget loads after the page is interactive so it does not delay first paint on landing pages.
const GlobalConciergeWidget = lazy(() => import('./components/GlobalConciergeWidget'));
import PatientStickyCta from './components/PatientStickyCta';
import { rememberAttribution, syncFlowCancerType } from './lib/attributionMemory';
import { onAdsRouteChange } from './lib/googleAdsTag';
import { onPixelRouteChange } from './lib/metaPixel';
import { NEW_LANDINGS } from './lib/cancerLandings';
import { logSiteVisit } from './lib/siteVisit';
import { releasePrerender } from './lib/prerenderRelease';
import { useAuth } from './contexts/AuthContext';
import { SITE_PORTAL_PATH, SITE_INSIGHTS_PATH, isSynergyPilotLogin } from './lib/sitePortalLanding';
const AdminSynergyProtocolReviews = lazy(() => import('./pages/admin-synergy-protocol-reviews/index'));
import IntakeRouteGuard from './components/IntakeRouteGuard';
import RoleDashboardRedirect, { SiteLoginRedirect, TrackRedirect } from './components/RoleDashboardRedirect';
// Direct import — guaranteed default-export resolution, avoids lazy empty-module failure
const SynergyPilot = lazy(() => import('./pages/synergy-pilot'));

// ─── Route-local error boundary for /synergy-pilot only ──────────────────────
class SynergyPilotErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0C' }}>
          <div className="max-w-md w-full p-8 rounded-2xl border border-gray-700 bg-gray-900/80 text-center space-y-4">
            <p className="text-gray-200 text-base font-medium">
              The Synergy pilot workspace could not load. No patient, protocol, referral, message, or external delivery action was performed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <a
                href="/clinic-login?next=/synergy-pilot"
                className="py-2.5 px-5 rounded-lg border border-gray-600 text-gray-300 text-sm hover:bg-gray-800 transition-colors"
              >
                Return to Pilot Sign-In
              </a>
              <button
                onClick={() => { this.setState({ hasError: false }); window.location?.reload(); }}
                className="py-2.5 px-5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
              >
                Retry Workspace
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── Route-level code splitting ──────────────────────────────────────────────
// Public pages
const HopeGateHomePage = lazy(() => import('./pages/hope-gate-home-page'));
const HopeFirst = lazy(() => import('./pages/hopefirst'));
const TrialDetailPage = lazy(() => import('./pages/hopefirst/components/TrialDetailPage'));
const ClinicalTrials = lazy(() => import('./pages/clinical-trials'));
const ConditionsExplorer = lazy(() => import('./pages/conditions'));
const TrialResources = lazy(() => import('./pages/trial-resources'));
const PatientRegistration = lazy(() => import('./pages/patient-registration'));
const CaseStatusPage = lazy(() => import('./pages/case-status'));
const PatientLogin = lazy(() => import('./pages/patient-login'));
const AuthCallback = lazy(() => import('./pages/auth-callback'));
const AuthContinue = lazy(() => import('./pages/auth-continue'));
const ShareRecord = lazy(() => import('./pages/share-record'));
const VoiceOptOut = lazy(() => import('./pages/voice-opt-out'));
const TrialAlertsUnsubscribe = lazy(() => import('./pages/trial-alerts-unsubscribe'));
const TrialJourney = lazy(() => import('./pages/trial-journey'));
const TrialPhysicianBrief = lazy(() => import('./pages/trial-physician-brief'));
const StaffFeedReview = lazy(() => import('./pages/staff-feed-review'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfUse = lazy(() => import('./pages/terms'));
const OrganizationAccessRequest = lazy(() => import('./pages/organization-access-request'));
const AboutUsContactUs = lazy(() => import('./pages/about-us-contact-us'));
const ClinicsHub = lazy(() => import('./pages/clinics-hub'));
const SponsorsHub = lazy(() => import('./pages/sponsors-hub'));
const PatientsHub = lazy(() => import('./pages/patients-hub'));
const CareersHub = lazy(() => import('./pages/careers-hub'));
const ClinicMatchAI = lazy(() => import('./pages/clinicmatch-ai'));
const TrialBridgeAI = lazy(() => import('./pages/trialbridge-ai'));
const OncoEchoAI = lazy(() => import('./pages/oncoecho-ai'));
const OncoEchoBilling = lazy(() => import('./pages/oncoecho-billing'));
const BiopharmaConsulting = lazy(() => import('./pages/biopharma-consulting'));
const ClinCore = lazy(() => import('./pages/clincore'));
const FellowshipPortal = lazy(() => import('./pages/fellowship-portal'));
const AIEMRIntegration = lazy(() => import('./pages/ai-emr-integration'));
const ReferralView = lazy(() => import('./pages/referral-view'));
const UnverifiedAccount = lazy(() => import('./pages/unverified-account'));
const VerifyEmail = lazy(() => import('./pages/verify-email'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Auth pages
const StaffLogin = lazy(() => import('./pages/staff-login'));
const ClinicLogin = lazy(() => import('./pages/clinic-login'));
const ClinicRegistration = lazy(() => import('./pages/clinic-registration'));
const ClinicPasswordSetup = lazy(() => import('./pages/clinic-password-setup'));
const ClinicFirstLogin = lazy(() => import('./pages/clinic-first-login'));
const SponsorLogin = lazy(() => import('./pages/sponsor-login'));
const SponsorRegistration = lazy(() => import('./pages/sponsor-registration'));
const ClinCoreLogin = lazy(() => import('./pages/clincore-login'));
const ClinCoreRegistration = lazy(() => import('./pages/clincore-registration'));

// ── Synthetic-only demo routes — no auth guard, browser-local key only ────────
const PatientDemoPage = lazy(() => import('./pages/patient-demo'));
const ClinCoreDemoPage = lazy(() => import('./pages/clincore-demo'));

// ClinCore authenticated learning catalogue — clincore role only
const ClinCoreLearning = lazy(() => import('./pages/clincore-learning'));

// ClinCore authenticated dashboard — clincore role only, least-privilege
const ClinCoreDashboard = lazy(() => import('./pages/clincore/ClinCoreDashboard'));

// Patient-protected pages
const PatientDashboard = lazy(() => import('./pages/patient-dashboard'));
const PatientMessaging = lazy(() => import('./pages/patient-messaging'));
const DocumentUploadHub = lazy(() => import('./pages/document-upload-hub'));
const StartLanding = lazy(() => import('./pages/start'));
const StartLung = lazy(() => import('./pages/start/lung'));
const StartCondition = lazy(() => import('./pages/start/StartCondition'));
const StartCancer = lazy(() => import('./pages/start/cancer'));
const AriaLanding = lazy(() => import('./pages/aria'));
const TalkLanding = lazy(() => import('./pages/talk'));
const TrialMatchingResults = lazy(() => import('./pages/trial-matching-results'));
const RecordRequests = lazy(() => import('./pages/record-requests'));

// Staff-protected pages
const AdministratorPortal = lazy(() => import('./pages/administrator-portal'));
const StaffInbox = lazy(() => import('./pages/staff-inbox'));
const UserManagement = lazy(() => import('./pages/user-management'));
const TrialDatabaseManager = lazy(() => import('./pages/trial-database-manager'));
const AdminPinnedTrials = lazy(() => import('./pages/admin-pinned-trials'));
const AdminReferralSla = lazy(() => import('./pages/admin-referral-sla'));
const AdminInsights = lazy(() => import('./pages/admin-insights'));
const AdminSiteContacts = lazy(() => import('./pages/admin-site-contacts'));
const AdminSiteOutreach = lazy(() => import('./pages/admin-site-outreach'));
const AdminPracticeReferrals = lazy(() => import('./pages/admin-practice-referrals'));
const AdminSiteProfiles = lazy(() => import('./pages/admin-site-profiles'));
const SiteOutreachUnsubscribe = lazy(() => import('./pages/site-outreach-unsubscribe'));
const EmailUnsubscribe = lazy(() => import('./pages/unsubscribe'));
const AdminInboundCalls = lazy(() => import('./pages/admin-inbound-calls'));
const PatientRematch = lazy(() => import('./pages/patient-rematch'));
const OncoEchoScribeSession = lazy(() => import('./pages/oncoecho-scribe-session'));
const ScribePatientsPage = lazy(() => import('./pages/scribe-history/PatientsPage'));
const ScribePatientChartPage = lazy(() => import('./pages/scribe-history/PatientChartPage'));
const ScribeClinicCalendarPage = lazy(() => import('./pages/scribe-history/ClinicCalendarPage'));
const ScribePrepQueuePage = lazy(() => import('./pages/scribe-history/PrepQueuePage'));
const ScribeTeamPage = lazy(() => import('./pages/scribe-history/TeamPage'));
const OncoEchoAdminQC = lazy(() => import('./pages/oncoecho-admin-qc'));
const OncoEchoTrialMatrix = lazy(() => import('./pages/oncoecho-trial-matrix'));
const BusinessDevelopmentIntelligence = lazy(() => import('./pages/business-development-intelligence'));
const ScreenFailBd = lazy(() => import('./pages/screen-fail-bd'));
const CampaignLandingBuilder = lazy(() => import('./pages/campaign-landing-builder'));
const AIAgentsControlCenter = lazy(() => import('./pages/ai-agents-control-center'));
const AICostDashboard = lazy(() => import('./pages/ai-cost-dashboard'));
const Settings = lazy(() => import('./pages/settings'));
const NurseVerificationSettings = lazy(() => import('./pages/nurse-verification-settings'));
const LeadTracker = lazy(() => import('./pages/lead-tracker'));
const StaffMatchingReview = lazy(() => import('./pages/staff-matching-review'));
const ClinicalLeadQA = lazy(() => import('./pages/clinical-lead-qa'));
const ClinicalLeadSandboxControls = lazy(() => import('./pages/clinical-lead-sandbox-controls'));
const ClinicalProfileReviewQA = lazy(() => import('./pages/clinical-profile-review-qa'));
const SyntheticFactLifecycleQA = lazy(() => import('./pages/synthetic-fact-lifecycle-qa'));
const ClinicianReviewDashboard = lazy(() => import('./pages/clinician-review-dashboard'));
const ClinicianReviewPreview = lazy(() => import('./pages/clinician-review-dashboard/preview'));

// Synthetic Documentation Draft — staff-only, frontend-only, no model/DB/network calls
const SyntheticDocumentationDraft = lazy(() => import('./pages/oncoecho-synthetic-documentation-draft'));

// Clinic-protected pages
const SitePortal = lazy(() => import('./pages/site-portal'));
const AriaResearchAssistantPage = lazy(() => import('./pages/aria-research-assistant'));
const SponsorPrescreenLogPage = lazy(() => import('./pages/aria-research-assistant/SponsorPrescreenLog'));
const AdminAriaResearchAssistant = lazy(() => import('./pages/admin-aria-research-assistant'));
const SiteRegistration = lazy(() => import('./pages/site-registration'));
const SiteOnboardPage = lazy(() => import('./pages/site-onboard'));
const VolunteerRegistration = lazy(() => import('./pages/volunteer'));

// Sponsor-protected pages
const SponsorDashboard = lazy(() => import('./pages/sponsor-dashboard'));
const SponsorAnalyticsDashboard = lazy(() => import('./pages/sponsor-analytics-dashboard'));
const SponsorPending = lazy(() => import('./pages/sponsor-pending'));

// Demo/investor pages
const InvestorDashboard = lazy(() => import('./pages/investor-dashboard'));

// P0-03: Organization pending approval page
const OrganizationPending = lazy(() => import('./pages/organization-pending'));

// New UNLAUNCHED candidate pages
const RegistriesPage = lazy(() => import('./pages/registries'));
const SmartFhirPage = lazy(() => import('./pages/smart-fhir'));
const TrialQueuesPage = lazy(() => import('./pages/trial-queues'));
const ResearchOperationsPage = lazy(() => import('./pages/research-operations'));

// Gate 2 Recipient isolated pages — gate2_recipient role only, before all fallbacks
const Gate2RecipientSetup = lazy(() => import('./pages/gate2-recipient-setup'));
const Gate2RecipientPortal = lazy(() => import('./pages/gate2-recipient-portal'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const AccountSetup = lazy(() => import('./pages/account-setup'));
const ReferPage = lazy(() => import('./pages/refer'));
const PhysicianUpdatesPage = lazy(() => import('./pages/physician-updates'));
const SiteTrainingPage = lazy(() => import('./pages/site-training'));
const PartnersPage = lazy(() => import('./pages/partners'));
const PartnerPage = lazy(() => import('./pages/partners/PartnerPage'));
const PartnerAccount = lazy(() => import('./pages/partners/PartnerAccount'));
const AdminCommunityPartners = lazy(() => import('./pages/admin-community-partners'));
const PartnerDemoBook = lazy(() => import('./pages/partner-demo/BookDemo'));
const PartnerDemoTry = lazy(() => import('./pages/partner-demo/TryDemo'));
const PartnerDemoManage = lazy(() => import('./pages/partner-demo/ManageBooking'));
const PartnerDemoRoom = lazy(() => import('./pages/partner-demo/DemoRoom'));
const PartnerDemoPreview = lazy(() => import('./pages/partner-demo/DemoPreview'));
const AdminPartnerDemos = lazy(() => import('./pages/admin-partner-demos'));
const AdminTrainingAcademy = lazy(() => import('./pages/admin-training-academy'));

/**
 * P0 Sprint 1: SponsorPendingGuard
 * Restricts /sponsor-pending STRICTLY to unverified Sponsor/CRO customer accounts.
 * - super_admin / staff → redirect to /sponsor-dashboard (never /sponsor-pending)
 * - Unauthenticated → redirect to /sponsor-login
 * - Verified sponsor (status !== 'pending_approval') → redirect to /sponsor-dashboard
 * - Unverified sponsor (status === 'pending_approval') → allow through
 */
const SponsorPendingGuard = ({ children }) => {
  const { user, userRole, isSuperuser, isStaff, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not authenticated → send to sponsor login
  if (!user) {
    return <Navigate to="/sponsor-login" replace />;
  }

  // P0 Sprint 1: super_admin and staff NEVER see /sponsor-pending — send to /sponsor-dashboard
  if (isSuperuser || isStaff) {
    return <Navigate to="/sponsor-dashboard" replace />;
  }

  // Only sponsor_cro and sponsor customer roles may see this page
  const sponsorRoles = ['sponsor_cro', 'sponsor'];
  if (!sponsorRoles?.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  // Verified sponsors (approved) should be on /sponsor-dashboard, not /sponsor-pending
  // The pending page is only for accounts still awaiting approval — allow through
  return children;
};

// ── Synergy Pilot tenant predicate — pure helper, UI routing only ─────────────
// Uses ONLY role and email. Does NOT require organization, display name,
// organization ID, URL parameter, local storage, or any client-controlled value.
export { isSynergyPilotLogin } from './lib/sitePortalLanding';

/**
 * SynergyPilotGuard
 * 1. Unauthenticated → /clinic-login?next=/synergy-pilot
 * 2. Demo / staff / super_admin → existing restricted redirects (unchanged)
 * 3. Authenticated site_clinic (any) → render portal; server get_my_membership is the sole tenant authority
 * 4. Every other authenticated role → their existing safe dashboard
 * Does NOT require userProfile.email, userProfile.organization, or any browser profile attribute to identify Synergy.
 */
const SynergyPilotGuard = ({ children }) => {
  const { user, userProfile, loading, profileLoading, isClinic, isSuperuser, isStaff, isDemoMode } = useAuth();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 1. Unauthenticated
  if (!user) {
    return <Navigate to="/clinic-login?next=/synergy-pilot" replace />;
  }

  // 2. Demo / staff / super_admin — existing restricted redirects unchanged
  if (isDemoMode) return <Navigate to="/investor-dashboard" replace />;
  if (isSuperuser || isStaff) return <Navigate to="/administrator-portal" replace />;

  // 3. Authenticated site_clinic — let the server-authoritative get_my_membership decide tenant access
  if (isClinic) {
    return children;
  }

  // 4. Every other authenticated role → their safe dashboard
  const role = userProfile?.role;
  if (role === 'sponsor_cro' || role === 'sponsor') return <Navigate to="/sponsor-dashboard" replace />;
  if (role === 'patient') return <Navigate to="/patient-dashboard" replace />;
  return <Navigate to="/" replace />;
};

/**
 * Authenticated site/clinic users land on Site Portal instead of the public
 * marketing home or patient-only pages (e.g. /trial-matching-results).
 * Patient, staff, sponsor, and guest destinations stay unchanged.
 */
const SiteAuthenticatedHome = ({ children }) => {
  const { user, loading, profileLoading, isClinic, isDemoMode, userProfile } = useAuth();

  if (loading || (user && profileLoading)) {
    return <PageLoader />;
  }

  if (user && isClinic && !isDemoMode) {
    if (isSynergyPilotLogin(userProfile)) {
      return <Navigate to="/synergy-pilot" replace />;
    }
    return <Navigate to={SITE_PORTAL_PATH} replace />;
  }

  return children;
};

// Mount children once the page has loaded and the browser is idle (max ~2.5s after load).
const AfterIdle = ({ children }) => {
  const [ready, setReady] = React.useState(false);
  useEffect(() => {
    let cancelled = false;
    let idleId = null;
    let timer = null;
    const go = () => { if (!cancelled) setReady(true); };
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') idleId = window.requestIdleCallback(go, { timeout: 2500 });
      else timer = setTimeout(go, 1200);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', schedule);
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId);
      if (timer) clearTimeout(timer);
    };
  }, []);
  return ready ? children : null;
};

// ── Suspense fallback ────────────────────────────────────────────────────────
const PageLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#0f0f1a",
      gap: "1.25rem",
    }}
  >
    {/* Spinner */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      style={{ animation: "spin 0.9s linear infinite" }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle
        cx="22"
        cy="22"
        r="18"
        fill="none"
        stroke="rgba(139,92,246,0.2)"
        strokeWidth="4"
      />
      <path
        d="M22 4 a18 18 0 0 1 18 18"
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
    <p
      style={{
        color: "#94a3b8",
        fontSize: "0.9375rem",
        fontFamily: "system-ui, -apple-system, sans-serif",
        letterSpacing: "0.01em",
      }}
    >
      Loading…
    </p>
  </div>
);

const AttributionCapture = () => {
  const { search, pathname } = useLocation();
  useEffect(() => {
    rememberAttribution(search);
    syncFlowCancerType(pathname, search);
    // Google Ads tag: allowlisted general pages only (src/lib/googleAdsTag.js); gclid kept first-party.
    onAdsRouteChange(pathname, search);
    // Meta pixel: same allowlist; drop the sandboxed frame when navigating to any other page.
    onPixelRouteChange(pathname);
    if (pathname === '/' || pathname === '/patient-registration' || pathname === '/upload') {
      logSiteVisit(pathname, 'landing');
    }
  }, [search, pathname]);
  return null;
};

// Pre-rendered pages (scripts/prerender-start.mjs) show a static snapshot until the live page
// mounts and calls releasePrerender(). If the app navigates elsewhere first (e.g. a redirect for a
// signed-in user), drop the snapshot right away so it never covers a different route.
const PrerenderReleaseOnNavigate = () => {
  const { pathname } = useLocation();
  const initial = React.useRef(pathname);
  React.useLayoutEffect(() => {
    if (pathname !== initial.current) releasePrerender();
  }, [pathname]);
  return null;
};

// ── Router ───────────────────────────────────────────────────────────────────
const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <PrerenderReleaseOnNavigate />
        <AuthReturnForwarder />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#1a1a2e', color: '#fff', border: '1px solid rgba(139,92,246,0.3)' },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <RouterRoutes>
            {/* ── Public routes ── */}
            <Route path="/" element={
              <SiteAuthenticatedHome>
                <HopeGateHomePage />
              </SiteAuthenticatedHome>
            } />
            <Route path="/hopefirst" element={<HopeFirst />} />
            <Route path="/hopefirst/trial/:nctId" element={<TrialDetailPage />} />
            <Route path="/clinical-trials" element={<ClinicalTrials />} />
            <Route path="/conditions" element={<ConditionsExplorer />} />
            {/* Plan-URL aliases — destinations keep their ProtectedRoute auth walls */}
            <Route path="/admin" element={<Navigate to="/administrator-portal" replace />} />
            <Route path="/admin-dashboard" element={<Navigate to="/administrator-portal" replace />} />
            <Route path="/clinic-dashboard" element={<Navigate to="/clinic-management-dashboard" replace />} />
            <Route path="/sites" element={<Navigate to="/clinicmatch-ai" replace />} />
            <Route path="/site-login" element={<SiteLoginRedirect />} />
            <Route path="/dashboard" element={<RoleDashboardRedirect />} />
            <Route path="/clinician-review" element={<Navigate to="/clinician-review-dashboard" replace />} />
            <Route path="/matching-review" element={<Navigate to="/staff-matching-review" replace />} />
            <Route path="/pinned-trials" element={<Navigate to="/admin-pinned-trials" replace />} />
            <Route path="/screen-fail-bd-agent" element={<Navigate to="/screen-fail-bd" replace />} />
            <Route path="/trial-resources" element={<TrialResources />} />
            <Route path="/patient-registration" element={<PatientRegistration />} />
            <Route path="/case-status/:caseRef" element={<CaseStatusPage />} />
            <Route path="/case-status" element={<CaseStatusPage />} />
            {/* /track is a legacy alias of the canonical /case-status page (301 in netlify.toml too) */}
            <Route path="/track/:caseRef" element={<TrackRedirect />} />
            <Route path="/track" element={<TrackRedirect />} />
            <Route path="/patient-login" element={<PatientLogin />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/continue" element={<AuthContinue />} />
            <Route path="/share/:token" element={<ShareRecord />} />
            <Route path="/voice-opt-out" element={<VoiceOptOut />} />
            <Route path="/trial-alerts/unsubscribe" element={<TrialAlertsUnsubscribe />} />
            <Route path="/site-outreach/unsubscribe" element={<SiteOutreachUnsubscribe />} />
            <Route path="/unsubscribe" element={<EmailUnsubscribe />} />
            <Route path="/trials/:nctId/journey" element={<TrialJourney />} />
            <Route path="/trials/:nctId/brief" element={<TrialPhysicianBrief />} />
            <Route path="/sms-opt-out" element={<Navigate to="/voice-opt-out#sms-opt-out" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/organization-access-request" element={<OrganizationAccessRequest />} />
            <Route path="/about-us-contact-us" element={<AboutUsContactUs />} />
            <Route path="/clinics" element={<ClinicsHub />} />
            <Route path="/clinics-hub" element={<Navigate to="/clinics" replace />} />
            <Route path="/sponsors" element={<SponsorsHub />} />
            <Route path="/patients" element={<PatientsHub />} />
            <Route path="/careers" element={<CareersHub />} />
            <Route path="/clinicmatch-ai" element={<ClinicMatchAI />} />
            <Route path="/trialbridge-ai" element={<TrialBridgeAI />} />
            <Route path="/oncoecho-ai" element={<OncoEchoAI />} />
            <Route path="/oncoecho-billing" element={<OncoEchoBilling />} />
            <Route path="/biopharma-consulting" element={<BiopharmaConsulting />} />
            <Route path="/clincore" element={<ClinCore />} />
            <Route path="/fellowship-portal" element={<FellowshipPortal />} />
            <Route path="/ai-emr-integration" element={<AIEMRIntegration />} />
            <Route path="/referral-view/:referralId" element={<ReferralView />} />
            <Route path="/referral-view" element={<ReferralView />} />
            <Route path="/unverified-account" element={<UnverifiedAccount />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* ── UNLAUNCHED candidate routes ── */}
            <Route path="/registries" element={<RegistriesPage />} />
            <Route path="/smart-fhir" element={<SmartFhirPage />} />

            {/* ── Gate 2 Recipient isolated routes — before all fallbacks ── */}
            {/* gate2_recipient role only; hard-false patient/site/handoff access;  */}
            {/* no retry/resend; no dashboard fallback; no patient data or records  */}
            <Route path="/gate2-recipient-setup" element={<Gate2RecipientSetup />} />
            <Route path="/gate2-recipient-portal" element={<Gate2RecipientPortal />} />

            {/* ── Password recovery routes — no auth required ── */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account-setup" element={<AccountSetup />} />

            {/* ── Auth routes ── */}
            <Route path="/staff-login" element={<StaffLogin />} />
            <Route path="/clinic-login" element={<ClinicLogin />} />
            <Route path="/clinic-registration" element={<ClinicRegistration />} />
            <Route path="/clinic-accept-invite" element={<ClinicPasswordSetup />} />
            <Route path="/clinic-password-setup" element={<ClinicPasswordSetup />} />
            <Route path="/clinic-first-login" element={<ClinicFirstLogin />} />
            <Route path="/sponsor-login" element={<SponsorLogin />} />
            <Route path="/sponsor-registration" element={<SponsorRegistration />} />
            <Route path="/site-registration" element={<SiteRegistration />} />
            <Route path="/volunteer" element={<VolunteerRegistration />} />
            <Route path="/clincore-login" element={<ClinCoreLogin />} />
            <Route path="/clincore-registration" element={<ClinCoreRegistration />} />

            {/* ── Synthetic-only demo routes — no auth guard, reads browser-local key only ── */}
            {/* These routes NEVER satisfy authentication, verification, authorization,        */}
            {/* provider access, or organization approval. Authenticated routes are unchanged. */}
            <Route path="/patient-demo" element={<PatientDemoPage />} />
            <Route path="/clincore-demo" element={<ClinCoreDemoPage />} />

            {/* ── Patient-protected routes (require auth + email verification) ── */}
            <Route path="/patient-dashboard" element={
              <ProtectedRoute redirectTo="/patient-login" allowedRoles={['patient']} superuserBypass={false} requiresVerification={false}>
                <PatientDashboard />
              </ProtectedRoute>
            } />
            <Route path="/staff-feed-review" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} redirectTo="/staff-login">
                <StaffFeedReview />
              </ProtectedRoute>
            } />
            <Route path="/patient-messaging" element={
              <ProtectedRoute redirectTo="/patient-login" allowedRoles={['patient']} superuserBypass={false} requiresVerification={true}>
                <PatientMessaging />
              </ProtectedRoute>
            } />
            {/* ── Document upload: public intake. Guard only blocks an explicit intake stop. ── */}
            <Route path="/document-upload-hub" element={
              <IntakeRouteGuard>
                <DocumentUploadHub />
              </IntakeRouteGuard>
            } />
            <Route path="/refer" element={<ReferPage lang="en" />} />
            <Route path="/es/referir" element={<ReferPage lang="es" />} />
            <Route path="/physician-updates" element={<PhysicianUpdatesPage />} />
            <Route path="/partners" element={<PartnersPage lang="en" />} />
            <Route path="/es/socios" element={<PartnersPage lang="es" />} />
            <Route path="/partners/account" element={<PartnerAccount />} />
            <Route path="/partners/:slug" element={<PartnerPage />} />
            <Route path="/partners/demo" element={<PartnerDemoBook />} />
            <Route path="/partners/demo/try" element={<PartnerDemoTry />} />
            <Route path="/partners/demo/manage" element={<PartnerDemoManage />} />
            <Route path="/partners/demo/room/:bookingId" element={<PartnerDemoRoom />} />
            <Route path="/partners/demo/preview/:id" element={<PartnerDemoPreview />} />
            <Route path="/start" element={<StartLanding />} />
            <Route path="/start/lung" element={<StartLung />} />
            {NEW_LANDINGS.map((c) => (
              <Route key={c.path} path={c.path} element={<StartCondition conditionKey={c.key} />} />
            ))}
            <Route path="/start/cancer" element={<StartCancer />} />
            <Route path="/aria" element={<AriaLanding />} />
            <Route path="/talk" element={<TalkLanding />} />
            <Route path="/upload" element={
              <IntakeRouteGuard>
                <DocumentUploadHub />
              </IntakeRouteGuard>
            } />
            {/* Patient results page: signed-in site users go to their Site Portal instead. */}
            <Route path="/trial-matching-results" element={<SiteAuthenticatedHome><TrialMatchingResults /></SiteAuthenticatedHome>} />
            <Route path="/match-now" element={<TrialMatchingResults />} />
            <Route path="/record-requests" element={
              <ProtectedRoute redirectTo="/patient-login" requiresVerification={true}>
                <RecordRequests />
              </ProtectedRoute>
            } />

            {/* ── Staff-protected routes — explicit internal staff role set ── */}
            {/* Internal staff roles: superadmin, staff only.                  */}
            {/* Excludes: patient, site_clinic, sponsor_cro, clincore, investor_demo, gate2_recipient */}
            <Route path="/administrator-portal" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdministratorPortal />
              </ProtectedRoute>
            } />
            <Route path="/staff-inbox" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <StaffInbox />
              </ProtectedRoute>
            } />
            <Route path="/user-management" element={
              <ProtectedRoute allowedRoles={['superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/trial-database-manager" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <TrialDatabaseManager />
              </ProtectedRoute>
            } />
            <Route path="/admin-ad-center" element={<Navigate to="/administrator-portal?tab=social" replace />} />
            <Route path="/admin-social-campaigns" element={<Navigate to="/administrator-portal?tab=social" replace />} />
            <Route path="/admin-insights" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminInsights />
              </ProtectedRoute>
            } />
            <Route path="/admin-site-profiles" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminSiteProfiles />
              </ProtectedRoute>
            } />
            <Route path="/admin-site-contacts" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminSiteContacts />
              </ProtectedRoute>
            } />
            <Route path="/admin-site-outreach" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminSiteOutreach />
              </ProtectedRoute>
            } />
            <Route path="/admin-community-partners" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminCommunityPartners />
              </ProtectedRoute>
            } />
            <Route path="/admin-practice-referrals" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminPracticeReferrals />
              </ProtectedRoute>
            } />
            <Route path="/admin-partner-demos" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminPartnerDemos />
              </ProtectedRoute>
            } />
            <Route path="/admin-training-academy" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminTrainingAcademy />
              </ProtectedRoute>
            } />
            <Route path="/admin-referral-sla" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminReferralSla />
              </ProtectedRoute>
            } />
            <Route path="/admin-inbound-calls" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminInboundCalls />
              </ProtectedRoute>
            } />
            <Route path="/admin-pinned-trials" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminPinnedTrials />
              </ProtectedRoute>
            } />
            <Route path="/patient-rematch" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <PatientRematch />
              </ProtectedRoute>
            } />
            <Route path="/trial-queues" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/trial-queues">
                <TrialQueuesPage />
              </ProtectedRoute>
            } />
            <Route path="/research-operations" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/research-operations">
                <ResearchOperationsPage />
              </ProtectedRoute>
            } />
            <Route path="/oncoecho-scribe-session" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <OncoEchoScribeSession />
              </ProtectedRoute>
            } />
            <Route path="/scribe/patients" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <ScribePatientsPage />
              </ProtectedRoute>
            } />
            <Route path="/scribe/patients/:patientId" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <ScribePatientChartPage />
              </ProtectedRoute>
            } />
            <Route path="/scribe/calendar" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <ScribeClinicCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/scribe/prep" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <ScribePrepQueuePage />
              </ProtectedRoute>
            } />
            <Route path="/scribe/team" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'site_clinic', 'clinic', 'coordinator']} superuserBypass={true} redirectTo="/staff-login">
                <ScribeTeamPage />
              </ProtectedRoute>
            } />
            <Route path="/oncoecho-admin-qc" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <OncoEchoAdminQC />
              </ProtectedRoute>
            } />
            <Route path="/oncoecho-trial-matrix" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <OncoEchoTrialMatrix />
              </ProtectedRoute>
            } />
            <Route path="/social-autopilot" element={<Navigate to="/administrator-portal?tab=social" replace />} />
            <Route path="/business-development-intelligence" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <BusinessDevelopmentIntelligence />
              </ProtectedRoute>
            } />
            <Route path="/screen-fail-bd" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <ScreenFailBd />
              </ProtectedRoute>
            } />
            <Route path="/campaign-landing-builder" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <CampaignLandingBuilder />
              </ProtectedRoute>
            } />
            <Route path="/ai-agents-control-center" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AIAgentsControlCenter />
              </ProtectedRoute>
            } />
            <Route path="/ai-cost-dashboard" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AICostDashboard />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute demoBlocked={true}>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/nurse-verification-settings" element={<Navigate to="/specialist-verification-settings" replace />} />
            <Route path="/specialist-verification-settings" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <NurseVerificationSettings />
              </ProtectedRoute>
            } />
            <Route path="/lead-tracker" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <LeadTracker />
              </ProtectedRoute>
            } />
            <Route path="/staff-matching-review" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/staff-matching-review">
                <StaffMatchingReview />
              </ProtectedRoute>
            } />
            <Route path="/clinician-review-dashboard" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin', 'superuser', 'admin', 'nurse', 'nurse_reviewer', 'coordinator', 'provider_clinician', 'clinician_reviewer', 'management', 'operations']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/clinician-review-dashboard">
                <ClinicianReviewDashboard />
              </ProtectedRoute>
            } />
            {import.meta.env.DEV && (
              <Route path="/dev/clinician-review-preview" element={<ClinicianReviewPreview />} />
            )}
            <Route path="/clinical-lead-qa" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/clinical-lead-qa">
                <ClinicalLeadQA />
              </ProtectedRoute>
            } />
            <Route path="/clinical-lead-sandbox-controls" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/clinical-lead-sandbox-controls">
                <ClinicalLeadSandboxControls />
              </ProtectedRoute>
            } />
            <Route path="/clinical-profile-review-qa" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/clinical-profile-review-qa">
                <ClinicalProfileReviewQA />
              </ProtectedRoute>
            } />
            <Route path="/admin/synthetic-fact-lifecycle-qa" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/admin/synthetic-fact-lifecycle-qa">
                <SyntheticFactLifecycleQA />
              </ProtectedRoute>
            } />
            <Route path="/oncoecho-synthetic-documentation-draft" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/oncoecho-synthetic-documentation-draft">
                <SyntheticDocumentationDraft />
              </ProtectedRoute>
            } />
            <Route path="/admin/synergy-protocol-reviews" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login?next=/admin/synergy-protocol-reviews">
                <AdminSynergyProtocolReviews />
              </ProtectedRoute>
            } />

            {/* ── Synergy Research Closed Pilot — guarded by SynergyPilotGuard (role + email only, no organization field) ── */}
            <Route path="/synergy-pilot" element={
              <SynergyPilotErrorBoundary>
                <SynergyPilotGuard>
                  <SynergyPilot />
                </SynergyPilotGuard>
              </SynergyPilotErrorBoundary>
            } />

            {/* ── Clinic-protected routes ── */}
            <Route path="/clinic-management-dashboard" element={
              <ProtectedRoute redirectTo="/clinic-login" allowedRoles={['site_clinic', 'clinic']} demoBlocked={true}>
                <Navigate to={SITE_PORTAL_PATH} replace />
              </ProtectedRoute>
            } />
            {/* The old hard-coded analytics mock is gone: site metrics live in Site Portal → Insights (real data only). */}
            <Route path="/clinic-performance-analytics" element={
              <ProtectedRoute redirectTo="/clinic-login" allowedRoles={['site_clinic', 'clinic']} demoBlocked={true}>
                <Navigate to={SITE_INSIGHTS_PATH} replace />
              </ProtectedRoute>
            } />
            <Route path="/site-portal" element={
              <ProtectedRoute redirectTo="/clinic-login" allowedRoles={['site_clinic', 'clinic']} demoBlocked={true}>
                <SitePortal />
              </ProtectedRoute>
            } />
            {/* Aria Trial Navigator: public offering, sponsor aggregate log, staff admin. Path lives in ARIA_RA_PATH. */}
            <Route path="/sites/onboard" element={<SiteOnboardPage />} />
            <Route path="/sites/training" element={<SiteTrainingPage />} />
            <Route path="/sites/aria-trial-navigator" element={<AriaResearchAssistantPage />} />
            <Route path="/sites/aria-research-assistant" element={<Navigate to="/sites/aria-trial-navigator" replace />} />
            <Route path="/sponsor-prescreen-log" element={
              <ProtectedRoute redirectTo="/sponsor-login" allowedRoles={['sponsor_cro', 'sponsor']} superuserBypass={true} demoBlocked={true} requiresSponsorApproval={true}>
                <SponsorPrescreenLogPage />
              </ProtectedRoute>
            } />
            <Route path="/admin-aria-research-assistant" element={
              <ProtectedRoute allowedRoles={['staff', 'superadmin']} superuserBypass={true} demoBlocked={true} productionOnly={true} redirectTo="/staff-login">
                <AdminAriaResearchAssistant />
              </ProtectedRoute>
            } />

            {/* ── Sponsor-protected routes ── */}
            {/*
              P0 Sprint 1: Super Admin Routing Fix
              - super_admin accessing /sponsor-dashboard → sees staff-management SponsorDashboard view (superuserBypass=true, no requiresSponsorApproval)
              - /sponsor-pending is STRICTLY for unverified Sponsor/CRO customer accounts only
                (allowedRoles=['sponsor_cro','sponsor'], superuserBypass=false, no requiresSponsorApproval on this route)
            */}
            <Route path="/sponsor-dashboard" element={
              <ProtectedRoute
                redirectTo="/sponsor-login"
                allowedRoles={['sponsor_cro', 'sponsor']}
                superuserBypass={true}
                demoBlocked={true}
                requiresSponsorApproval={true}
              >
                <SponsorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/sponsor-analytics-dashboard" element={
              <ProtectedRoute
                redirectTo="/sponsor-login"
                allowedRoles={['sponsor_cro', 'sponsor']}
                superuserBypass={true}
                demoBlocked={true}
                requiresSponsorApproval={true}
              >
                <SponsorAnalyticsDashboard />
              </ProtectedRoute>
            } />

            {/* ── Sponsor pending status page — strictly unverified Sponsor/CRO customer accounts only ── */}
            {/*
              P0 Sprint 1: /sponsor-pending is restricted to sponsor_cro/sponsor roles with pending_approval status.
              super_admin and staff are NEVER redirected here — they always get direct access to /sponsor-dashboard.
            */}
            <Route path="/sponsor-pending" element={
              <SponsorPendingGuard>
                <SponsorPending />
              </SponsorPendingGuard>
            } />

            {/* P0-03: Organization pending approval — for unapproved clinic/sponsor registrations */}
            <Route path="/organization-pending" element={<OrganizationPending />} />

            {/* ── Demo/investor route ── */}
            <Route path="/investor-dashboard" element={
              <ProtectedRoute demoAllowed={true} redirectTo="/staff-login">
                <InvestorDashboard />
              </ProtectedRoute>
            } />

            {/* ── ClinCore authenticated learning catalogue — clincore + fellow aliases + Super Admin ── */}
            <Route path="/clincore-learning" element={
              <ProtectedRoute
                redirectTo="/clincore-login"
                allowedRoles={['clincore', 'fellow']}
                superuserBypass={true}
                demoBlocked={true}
                productionOnly={true}
              >
                <ClinCoreLearning />
              </ProtectedRoute>
            } />

            {/* ── ClinCore authenticated dashboard — clincore + fellow aliases + Super Admin ── */}
            <Route path="/clincore/dashboard" element={
              <ProtectedRoute
                redirectTo="/clincore-login"
                allowedRoles={['clincore', 'fellow']}
                superuserBypass={true}
                demoBlocked={true}
                productionOnly={true}
              >
                <ClinCoreDashboard />
              </ProtectedRoute>
            } />

            {/* ── 404 ── */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>

        <AttributionCapture />
        <PatientStickyCta />
        {/* Global AI Concierge — fixed launcher on every viewport, including phones */}
        <AfterIdle>
          <Suspense fallback={null}>
            <GlobalConciergeWidget />
          </Suspense>
        </AfterIdle>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;